import { memo, useEffect } from "react";
import { useAtom, useAtomValue } from 'jotai';
import { Spin } from 'antd';
import {
  landsatMosaicItemsLEFTAtom,
  landsatMosaicItemsRIGHTAtom,
  landsatMosaicLoadingAtom,
} from "./store";
import { API_URL_ADMIN } from "~/config/config";
import { LandsatMosaic_Item } from "~/common/typescript/apis/RemoteImageAlertAPIs";

const LandsatMosaicMapController = () => {
  const [itemsLeft, setItemsLeft] = useAtom(landsatMosaicItemsLEFTAtom);
  const [itemsRight, setItemsRight] = useAtom(landsatMosaicItemsRIGHTAtom);
  const loading = useAtomValue(landsatMosaicLoadingAtom);

  const setupTransformRequest = (map: any) => {
    if (!map) return;
    const jwt = localStorage.getItem('jwt');
    if (!jwt) return;

    map.setTransformRequest((url: string, resourceType: any) => {
      if (
        resourceType === 'Tile' &&
        (url.includes('/landsat-mosaic') || url.includes('/remote-image-alert-ext/'))
      ) {
        return {
          url,
          headers: { Authorization: `Bearer ${jwt}` },
        };
      }
      return { url };
    });
  };

  const syncLayers = async (
    map: any,
    items: LandsatMosaic_Item[],
    prefix: string,
  ) => {
    if (!map) return;

    setupTransformRequest(map);

    // 1. Remove layers not in list
    const style = map.getStyle();
    if (style && style.layers) {
      style.layers.forEach((l: any) => {
        if (l.id.startsWith(prefix)) {
          const id = l.id.replace(prefix, '');
          if (!items.find((i) => String(i.id) === id)) {
            if (map.getLayer(l.id)) {
              map.removeLayer(l.id);
            }
            if (map.getSource(l.id)) {
              map.removeSource(l.id);
            }
          }
        }
      });
    }

    // 2. Add layers in list
    for (const item of items) {
      const layerId = `${prefix}${item.id}`;
      if (!map.getLayer(layerId)) {
        let tileUrl = item.tileUrl;
        if (!tileUrl) {
          tileUrl = `${API_URL_ADMIN}/api/remote-image-alert-ext/landsat-mosaic-bimonthly/tile/{z}/{x}/{y}?image_id=${encodeURIComponent(item.id)}`;
        } else if (tileUrl.startsWith('/')) {
          tileUrl = `${API_URL_ADMIN}${tileUrl}`;
        } else {
          tileUrl = tileUrl.replace('http://localhost:11000', API_URL_ADMIN).replace('http://localhost:12000', API_URL_ADMIN);
        }

        const beforeId = map.getLayer('River labels') ? 'River labels' : undefined;

        try {
          map.addLayer(
            {
              id: layerId,
              type: 'raster',
              source: {
                type: 'raster',
                tiles: [tileUrl],
                tileSize: 256,
              },
              paint: {
                'raster-opacity': 1,
              },
            },
            beforeId
          );
        } catch (err) {
          console.error('[LandsatMosaicMapController] Error adding layer', layerId, err);
        }
      }
    }
  };

  useEffect(() => {
    syncLayers(window.beforeMap, itemsLeft, 'landsat-mosaic-layer-left-');
  }, [itemsLeft]);

  useEffect(() => {
    syncLayers(window.map, itemsRight, 'landsat-mosaic-layer-right-');
  }, [itemsRight]);

  return (
    <>
      {loading.isLoading && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 99999,
            backgroundColor: 'rgba(0, 0, 0, 0.82)',
            color: '#ffffff',
            padding: '20px 32px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(6px)',
            pointerEvents: 'all',
          }}
        >
          <Spin size="large" />
          <span style={{ fontSize: '15px', fontWeight: 500, letterSpacing: '0.3px' }}>
            {loading.text || 'Đang tải ảnh Landsat Ghép Định Kỳ (CDSE)...'}
          </span>
        </div>
      )}
    </>
  );
};

export default memo(LandsatMosaicMapController);
